'use client';

import { useState } from 'react';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/hooks/use-notifications';
import { useSession } from '@/hooks/use-session';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { LocaleLink } from '@/components/locale-link';
import { NotificationRow } from './NotificationRow';

/** How many rows the dropdown shows before deferring to the history page. */
const DROPDOWN_LIMIT = 10;

/**
 * Header notification bell — unread badge plus a dropdown of the most recent
 * items, with the full history one click away at /account/notifications.
 *
 * Renders nothing for guests (they have no notifications and the header is
 * width-measured — see header.tsx's fit probe).
 */
export function NotificationsBell() {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const { items, unreadCount, markRead, markAllRead, isMutating } =
    useNotifications();

  if (!session?.user?.sub) return null;

  const visible = items.slice(0, DROPDOWN_LIMIT);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('notifications.bell.ariaLabel', {
            n: unreadCount,
            defaultValue: 'Notifications ({{n}} unread)',
          })}
          className={cn(
            'relative inline-flex size-9 cursor-pointer items-center justify-center rounded-full border transition-colors',
            'border-[var(--fd-line-2)] text-[var(--fd-ink-2)] hover:bg-[var(--fd-bg-2)]',
          )}
        >
          <Bell className="size-4" aria-hidden />
          {unreadCount > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 inline-flex min-w-[17px] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-[17px] text-white"
              style={{ background: 'var(--fd-accent-ink, #c2410c)' }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(22rem,calc(100vw-2rem))] p-0"
      >
        <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
          <p className="text-sm font-semibold">
            {t('notifications.title', 'Notifications')}
          </p>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              disabled={isMutating}
              onClick={() => markAllRead()}
            >
              {t('notifications.markAllRead', 'Mark all read')}
            </Button>
          )}
        </div>

        {visible.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            {t('notifications.empty', 'Nothing new right now.')}
          </p>
        ) : (
          <ul className="max-h-[60vh] divide-y overflow-y-auto">
            {visible.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onMarkRead={markRead}
                disabled={isMutating}
                onNavigate={() => setOpen(false)}
              />
            ))}
          </ul>
        )}

        <div className="border-t px-4 py-2">
          <LocaleLink
            href="/account/notifications"
            onClick={() => setOpen(false)}
            className="block py-1 text-center text-sm font-medium hover:underline"
          >
            {t('notifications.seeAll', 'See all notifications')}
          </LocaleLink>
        </div>
      </PopoverContent>
    </Popover>
  );
}
